/**
 * Centralized API client for Read-Along Monitoring Dashboard
 * Handles all backend communication
 * Returns aggregated data ready for charts
 */

import tokenManager from './tokenManager';

const API_BASE = process.env.REACT_APP_RESOURCE_API || 'https://neev-api.onrender.com';

class ReadAlongAPI {
  /**
   * Get dashboard overview (landing page metrics)
   * @param {Object} params - { period: 'week'|'month', date: 'YYYY-MM-DD', cluster: 'All', medium: 'All', type: 'All' }
   * @returns {Promise<Object>} DashboardOverviewResponse
   */
  async getDashboardOverview(params = {}) {
    const query = new URLSearchParams({
      period: params.period || 'week',
      date: params.date || this.getCurrentWeekMonday(),
      cluster: params.cluster || 'All',
      medium: params.medium || 'All',
      type: params.type || 'All'
    });
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/dashboard/overview?${query}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch overview' }));
      throw new Error(error.error || error.details || 'Failed to fetch overview');
    }
    return response.json();
  }

  /**
   * Get month summary
   * @param {string} month - 'YYYY-MM' format
   * @param {Object} filters - { cluster: 'All', medium: 'All', type: 'All' }
   * @returns {Promise<Object>} MonthSummaryResponse
   */
  async getMonthSummary(month, filters = {}) {
    const query = new URLSearchParams({
      month,
      cluster: filters.cluster || 'All',
      medium: filters.medium || 'All',
      type: filters.type || 'All'
    });
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/dashboard/month-summary?${query}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch month summary' }));
      throw new Error(error.error || error.details || 'Failed to fetch month summary');
    }
    return response.json();
  }

  /**
   * Get week summary
   * @param {string} weekStart - 'YYYY-MM-DD' format (Monday)
   * @param {Object} filters - { cluster: 'All', medium: 'All', type: 'All' }
   * @returns {Promise<Object>} WeekSummaryResponse
   */
  async getWeekSummary(weekStart, filters = {}) {
    const query = new URLSearchParams({
      week_start: weekStart,
      cluster: filters.cluster || 'All',
      medium: filters.medium || 'All',
      type: filters.type || 'All'
    });
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/dashboard/week-summary?${query}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch week summary' }));
      throw new Error(error.error || error.details || 'Failed to fetch week summary');
    }
    return response.json();
  }

  /**
   * Get school detail
   * @param {number} schoolId - School ID
   * @param {string} weekStart - 'YYYY-MM-DD' format (Monday)
   * @returns {Promise<Object>} SchoolDetailResponse
   */
  async getSchoolDetail(schoolId, weekStart) {
    const query = new URLSearchParams({
      week_start: weekStart || this.getCurrentWeekMonday()
    });
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/dashboard/school/${schoolId}?${query}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch school detail' }));
      throw new Error(error.error || error.details || 'Failed to fetch school detail');
    }
    return response.json();
  }

  /**
   * Get available weeks
   * @param {Object} params - { start_date?: 'YYYY-MM-DD', end_date?: 'YYYY-MM-DD' }
   * @returns {Promise<Array>} Array of Week objects
   */
  async getWeeks(params = {}) {
    const query = new URLSearchParams();
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/weeks?${query}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch weeks' }));
      throw new Error(error.error || error.details || 'Failed to fetch weeks');
    }
    const data = await response.json();
    return data.weeks || [];
  }

  /**
   * Get available months
   * @returns {Promise<Array>} Array of month strings in 'YYYY-MM' format
   */
  async getMonths() {
    const response = await fetch(`${API_BASE}/api/v1/read-along/months`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch months' }));
      throw new Error(error.error || error.details || 'Failed to fetch months');
    }
    const data = await response.json();
    return data.months || [];
  }

  /**
   * Utility: Get current week Monday in YYYY-MM-DD format
   * @returns {string}
   */
  getCurrentWeekMonday() {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const diff = day === 0 ? -6 : 1 - day; // Monday is day 1
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    return monday.toISOString().split('T')[0];
  }

  /**
   * Utility: Get current month in YYYY-MM format
   * @returns {string}
   */
  getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  // ==================== Schools API ====================
  
  /**
   * Get auth token from localStorage
   */
  getAuthToken() {
    const token = localStorage.getItem("neev-auth-token");
    // Check if user has old token format - prompt to re-login
    if (token === "valid-session") {
      console.warn("Old token format detected. Please log out and log back in to get API key.");
      // Could redirect to login, but for now just warn
    }
    return token;
  }

  /**
   * List schools with filters
   * @param {Object} params - { academicYear, regionType, managementType, cluster, page, pageSize }
   */
  async listSchools(params = {}) {
    const query = new URLSearchParams();
    if (params.academicYear) query.append('academic_year', params.academicYear);
    if (params.regionType) query.append('region_type', params.regionType);
    if (params.managementType) query.append('management_type', params.managementType);
    if (params.cluster) query.append('cluster', params.cluster);
    if (params.jefCoordinator) query.append('jefCoordinator', params.jefCoordinator);
    if (params.search) query.append('search', params.search);
    // Always pass page and pageSize to ensure backend uses pagination
    query.append('page', params.page || 1);
    query.append('page_size', params.pageSize || 20);
    if (params.sort) query.append('sort', params.sort);
    
    // Use the correct endpoint: /schools/list (not /admin/schools)
    const response = await fetch(`${API_BASE}/api/v1/read-along/schools/list?${query}`);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch schools' }));
      throw new Error(error.error || error.details || 'Failed to fetch schools');
    }
    return response.json();
  }

  /**
   * Get school by ID
   * @param {number} id - School ID
   */
  async getSchool(id) {
    const response = await fetch(`${API_BASE}/api/v1/read-along/schools/by-id/${id}`);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch school' }));
      throw new Error(error.error || error.details || 'Failed to fetch school');
    }
    return response.json();
  }

  /**
   * Create school
   * @param {Object} schoolData - School data
   */
  async createSchool(schoolData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/schools`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(schoolData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to create school' }));
      throw new Error(error.error || error.details || 'Failed to create school');
    }
    return response.json();
  }

  /**
   * Update school
   * @param {number} id - School ID
   * @param {Object} schoolData - School data
   */
  async updateSchool(id, schoolData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/schools/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(schoolData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to update school' }));
      throw new Error(error.error || error.details || 'Failed to update school');
    }
    return response.json();
  }

  /**
   * Deactivate school (soft delete)
   * @param {number} id - School ID
   * @param {Object} schoolData - Optional school data from list (to avoid extra API call)
   */
  /**
   * Reactivate a school
   * @param {number} id - School ID
   * @param {Object} schoolData - Optional school data (to avoid extra API call)
   */
  async reactivateSchool(id, schoolData = null) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    let school = schoolData;
    
    // If school data not provided, fetch it
    if (!school) {
      try {
        const schoolResponse = await this.getSchool(id);
        school = schoolResponse.school || schoolResponse;
        if (!school) {
          throw new Error("School not found");
        }
      } catch (error) {
        throw new Error("Failed to get school data for reactivation: " + error.message);
      }
    }
    
    // Ensure we have classes - if empty, we need at least one for backend validation
    const classes = school.classes && school.classes.length > 0 
      ? school.classes.map(c => typeof c === 'object' ? c.name : c)
      : ["1"]; // Default to class 1 if no classes exist
    
    // Update with is_active = true, preserving all required fields including classes
    const updateData = {
      name: school.name,
      code: school.code,
      cluster: school.cluster || "",
      region_type: school.region_type || "",
      management_type: school.management_type || "",
      principal_id: school.principal?.id || school.principal_id || null,
      cluster_officer_id: school.cluster_officer?.id || school.cluster_officer_id || null,
      jef_coordinator_id: school.jef_coordinator?.id || school.jef_coordinator_id || null,
      is_active: true, // Reactivate
      academic_year: school.academic_year || "",
      classes: classes
    };
    
    return this.updateSchool(id, updateData);
  }

  async deactivateSchool(id, schoolData = null) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    let school = schoolData;
    
    // If school data not provided, fetch it
    if (!school) {
      try {
        const schoolResponse = await this.getSchool(id);
        school = schoolResponse.school || schoolResponse;
        if (!school) {
          throw new Error("School not found");
        }
      } catch (error) {
        throw new Error("Failed to get school data for deactivation: " + error.message);
      }
    }
    
    // Ensure we have classes - if empty, we need at least one for backend validation
    // For deactivation, we'll use the existing classes or default to ["1"] if none exist
    const classes = school.classes && school.classes.length > 0 
      ? school.classes 
      : ["1"]; // Default to class 1 if no classes exist (backend requires at least one)
    
    // Update with is_active = false, preserving all required fields including classes
    const updateData = {
      name: school.name,
      code: school.code,
      category: school.category,
      cluster: school.cluster,
      region_type: school.region_type,
      management_type: school.management_type,
      type: school.type,
      principal_id: school.principal_id,
      cluster_officer_id: school.cluster_officer_id,
      jef_coordinator_id: school.jef_coordinator_id,
      classes: classes, // Include classes (required by backend)
      is_active: false
    };
    
    // Include academic_year if available in school response
    if (school.academic_year) {
      updateData.academic_year = school.academic_year;
    }
    
    return this.updateSchool(id, updateData);
  }

  /**
   * Permanently delete an inactive school
   * @param {number} id - School ID
   */
  async deleteSchool(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/schools/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `Failed to delete school: ${response.statusText}`);
    }

    return await response.json();
  }

  async downloadSchoolsTemplate() {
    // Generate template CSV client-side in the exact format expected by bulk upload
    // IMPORTANT: Header names must match bulkUploadConfig.schools.requiredFields and rowMapper
    const instructions = [
      '# School Bulk Upload Template',
      '# Instructions:',
      '# - Required columns: School Name, Region Type, Management Type, Classes',
      '# - Optional columns: Cluster, Principal Code, Cluster Officer Code, JEF Coordinator Code, Is Active',
      '# - Region Type: rural or urban (case-insensitive)',
      '# - Management Type: Zilla Parishad, Municipal, Private, Aided (case-insensitive)',
      '# - Valid Region Types: rural, urban (case-insensitive)',
      '# - Valid Management Types: Zilla Parishad, Municipal, Private, Aided (case-insensitive)',
      '# - Classes: Comma-separated numbers 1-9 (e.g., 1,3,5)',
      '# - Is Active: TRUE or FALSE (default: TRUE if empty)',
      ''
    ];
    
    // Column structure MUST match bulkUploadConfig.schools.rowMapper expectations
    const headers = [
      'School Name',
      'Region Type',
      'Management Type',
      'Cluster',
      'Classes',
      'Principal Code',
      'Cluster Officer Code',
      'JEF Coordinator Code',
      'Is Active'
    ];
    
    // Example Row 1 - Basic School (no staff)
    const exampleRow1 = [
      'Example Primary School',
      'rural',
      'Zilla Parishad',
      'Block A',
      '1,3,5',
      '',
      '',
      '',
      '',
      '',
      '',
      'TRUE'
    ];
    
    // Example Row 2 - School with existing staff codes (from Teachers/Officers lists)
    const exampleRow2 = [
      'Another School',
      'urban',
      'Municipal',
      'Block B',
      '2,4',
      'PRIN001',
      'CO001',
      'JEF001',
      'TRUE'
    ];
    
    // Example Row 3 - School with no staff codes and inactive status
    const exampleRow3 = [
      'Third School',
      'rural',
      'Zilla Parishad',
      'Block C',
      '1,2,3',
      '',
      '',
      '',
      'FALSE'
    ];
    
    // Create CSV with instructions, headers, and three example rows
    const csvContent = [
      ...instructions,
      headers.join(','),
      exampleRow1.join(','),
      exampleRow2.join(','),
      exampleRow3.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return blob;
  }

  /**
   * Get academic years
   */
  async getAcademicYears() {
    const response = await fetch(`${API_BASE}/api/v1/read-along/academic-years`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch academic years' }));
      throw new Error(error.error || error.details || 'Failed to fetch academic years');
    }
    return response.json();
  }

  /**
   * Get current academic year
   */
  async getCurrentAcademicYear() {
    const response = await fetch(`${API_BASE}/api/v1/read-along/academic-years/current`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch current academic year' }));
      throw new Error(error.error || error.details || 'Failed to fetch current academic year');
    }
    const data = await response.json();
    // API returns { academic_year: { code: "...", ... } }
    return data.academic_year?.code || null;
  }

  /**
   * Get distinct clusters for schools
   * @param {string} academicYear - Academic year code
   */
  async getSchoolClusters(academicYear) {
    const response = await fetch(`${API_BASE}/api/v1/read-along/schools/clusters?academic_year=${academicYear}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch clusters' }));
      throw new Error(error.error || error.details || 'Failed to fetch clusters');
    }
    return response.json();
  }

  /**
   * Get list of teachers
   * @param {boolean} activeOnly - Only return active teachers
   */
  /**
   * Get list of teachers
   * @param {boolean} activeOnly - Only return active teachers
   * @param {Object} params - Optional pagination and search params: { page, pageSize, search }
   */
  async getTeachers(activeOnly = true, params = {}) {
    const query = new URLSearchParams();
    query.append('active_only', activeOnly);
    // Always pass page and pageSize to ensure backend uses pagination
    query.append('page', params.page || 1);
    query.append('page_size', params.pageSize || 20);
    if (params.search) query.append('search', params.search);
    if (params.sort) query.append('sort', params.sort);
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/teachers?${query}`, {
      headers: {
        'X-API-Key': this.getAuthToken()
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch teachers' }));
      throw new Error(error.error || error.details || 'Failed to fetch teachers');
    }
    return response.json();
  }

  /**
   * Get list of officers
   * @param {string} role - Filter by role (e.g., 'cluster_officer', 'jef_coordinator')
   * @param {boolean} activeOnly - Only return active officers
   * @param {Object} params - Additional parameters { page, pageSize, search, sort }
   */
  async getOfficers(role = '', activeOnly = true, params = {}) {
    const query = new URLSearchParams();
    if (role) query.append('role', role);
    query.append('active_only', activeOnly);
    // Always pass page and pageSize to ensure backend uses pagination
    query.append('page', params.page || 1);
    query.append('page_size', params.pageSize || 20);
    if (params.search) query.append('search', params.search);
    if (params.sort) query.append('sort', params.sort);
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/officers?${query}`, {
      headers: {
        'X-API-Key': this.getAuthToken()
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch officers' }));
      throw new Error(error.error || error.details || 'Failed to fetch officers');
    }
    return response.json();
  }

  /**
   * Create a new teacher
   * @param {Object} teacherData - Teacher data { name, phone, email, alternate_phone, notes, is_active }
   */
  async createTeacher(teacherData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/teachers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(teacherData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to create teacher' }));
      // Include details in error message for better debugging
      const errorMessage = error.error || error.details || 'Failed to create teacher';
      const fullMessage = error.details ? `${errorMessage}: ${error.details}` : errorMessage;
      throw new Error(fullMessage);
    }
    
    const data = await response.json();
    // Handle both wrapped and unwrapped responses
    return data.teacher || data;
  }

  /**
   * Update a teacher
   * @param {number} id - Teacher ID
   * @param {Object} teacherData - Teacher data { name, phone, email, alternate_phone, notes, is_active }
   */
  async updateTeacher(id, teacherData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/teachers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(teacherData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to update teacher' }));
      throw new Error(error.error || error.details || 'Failed to update teacher');
    }
    return response.json();
  }

  /**
   * Deactivate a teacher (soft delete by setting is_active to false)
   * @param {number} id - Teacher ID
   */
  async deactivateTeacher(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    // Get current teacher data and update is_active
    try {
      const response = await this.getTeachers(false);
      const teacher = response.teachers?.find(t => t.id === id);
      if (!teacher) {
        throw new Error("Teacher not found");
      }
      return this.updateTeacher(id, { ...teacher, is_active: false });
    } catch (error) {
      // If we can't get the teacher, just try to update with is_active false
      return this.updateTeacher(id, { is_active: false });
    }
  }

  /**
   * Reactivate a teacher
   * @param {number} id - Teacher ID
   * @param {Object} teacherData - Optional teacher data (to avoid extra API call)
   */
  async reactivateTeacher(id, teacherData = null) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    // Get current teacher data if not provided
    let teacher = teacherData;
    if (!teacher) {
      try {
        const response = await this.getTeachers(false);
        teacher = response.teachers?.find(t => t.id === id);
        if (!teacher) {
          throw new Error("Teacher not found");
        }
      } catch (error) {
        throw new Error("Failed to get teacher data for reactivation: " + error.message);
      }
    }
    
    // Update with is_active = true, preserving all other fields
    return this.updateTeacher(id, { ...teacher, is_active: true });
  }

  /**
   * Permanently delete an inactive teacher
   * @param {number} id - Teacher ID
   */
  async deleteTeacher(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/teachers/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `Failed to delete teacher: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Create a new officer
   * @param {Object} officerData - Officer data { name, role, phone, email, alternate_phone, notes, is_active }
   */
  async createOfficer(officerData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/officers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(officerData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to create officer' }));
      throw new Error(error.error || error.details || 'Failed to create officer');
    }
    return response.json();
  }

  /**
   * Update an officer
   * @param {number} id - Officer ID
   * @param {Object} officerData - Officer data { name, role, phone, email, alternate_phone, notes, is_active }
   */
  async updateOfficer(id, officerData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/officers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(officerData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to update officer' }));
      throw new Error(error.error || error.details || 'Failed to update officer');
    }
    return response.json();
  }

  /**
   * Deactivate an officer (soft delete by setting is_active to false)
   * @param {number} id - Officer ID
   * @param {Object} officerData - Optional officer data from list (to avoid extra API call)
   */
  async deactivateOfficer(id, officerData = null) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    let officer = officerData;
    
    // If officer data not provided, fetch it
    if (!officer) {
      try {
        const response = await this.getOfficers('', false);
        officer = response.officers?.find(o => o.id === id);
        if (!officer) {
          throw new Error("Officer not found");
        }
      } catch (error) {
        // If we can't get the officer, just try to update with is_active false
        return this.updateOfficer(id, { is_active: false });
      }
    }
    
    // Update with is_active = false, preserving all existing fields
    return this.updateOfficer(id, {
      name: officer.name,
      role: officer.role,
      phone: officer.phone,
      email: officer.email,
      alternate_phone: officer.alternate_phone,
      notes: officer.notes,
      is_active: false
    });
  }

  /**
   * Permanently delete an inactive officer
   * @param {number} id - Officer ID
   */
  async deleteOfficer(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/officers/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `Failed to delete officer: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Reactivate an inactive officer
   * @param {number} id - Officer ID
   * @param {Object} officerData - Officer data (optional, will be fetched if not provided)
   */
  async reactivateOfficer(id, officerData = null) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    // Get current officer data if not provided
    let officer = officerData;
    if (!officer) {
      try {
        const response = await this.getOfficers('', false);
        officer = response.officers?.find(o => o.id === id);
        if (!officer) {
          throw new Error("Officer not found");
        }
      } catch (error) {
        throw new Error("Failed to get officer data for reactivation: " + error.message);
      }
    }
    
    // Update with is_active = true, preserving all other fields
    return this.updateOfficer(id, { ...officer, is_active: true });
  }

  /**
   * Download teachers template CSV
   */
  async downloadTeachersTemplate() {
    // Instructions as CSV comments
    const instructions = [
      '# Teachers Bulk Upload Template',
      '# Instructions:',
      '# - Required fields are marked with *',
      '# - Phone number is required for new teachers (for authentication)',
      '# - If a teacher with the same phone number exists, it will be updated',
      '# - Address field is optional',
      '# - Is Active: TRUE or FALSE (default: TRUE)',
      '#',
      '# Example rows:',
      '# Row 1: Basic teacher with required fields',
      '# Row 2: Teacher with all optional fields',
      '# Row 3: Update existing teacher (by phone number)',
      '#'
    ];
    
    const headers = [
      'Name',
      'Phone',
      'Email',
      'Alternate Phone',
      'Address',
      'Notes',
      'Is Active'
    ];
    
    const exampleRow1 = [
      'John Doe',
      '9876543210',
      '',
      '',
      '',
      '',
      'TRUE'
    ];
    
    const exampleRow2 = [
      'Jane Smith',
      '9876543211',
      'jane.smith@example.com',
      '9876543212',
      '123 Main Street, City',
      'Principal of Example School',
      'TRUE'
    ];
    
    const exampleRow3 = [
      'John Doe Updated',
      '9876543210',
      'john.doe@example.com',
      '',
      '456 New Street',
      'Updated information',
      'TRUE'
    ];
    
    const csvContent = [
      ...instructions,
      headers.join(','),
      exampleRow1.join(','),
      exampleRow2.join(','),
      exampleRow3.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return blob;
  }

  /**
   * Download officers template CSV
   */
  async downloadOfficersTemplate() {
    const headers = [
      'Name',
      'Role',
      'Phone',
      'Email',
      'Alternate Phone',
      'Notes',
      'Is Active'
    ];
    
    const exampleRow = [
      'Jane Smith',
      'cluster_officer',
      '9876543220',
      'jane.smith@example.com',
      '9876543221',
      'Cluster Officer for Block A',
      'TRUE'
    ];
    
    const csvContent = [
      headers.join(','),
      exampleRow.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return blob;
  }

  /**
   * Download JEF volunteers template CSV
   */
  async downloadJEFVolunteersTemplate() {
    // Instructions as CSV comments
    const instructions = [
      '# JEF Volunteers & Coordinators Bulk Upload Template',
      '# Instructions:',
      '# - Required fields: Name, Phone',
      '# - Phone number is required (for authentication and identification)',
      '# - Phone numbers can include spaces, +91 prefix - will be normalized automatically',
      '# - If an officer with the same phone number exists, it will be updated',
      '# - Role: Optional, defaults to "jef_coordinator" if empty',
      '#   Valid roles: jef_coordinator, volunteer, cluster_officer, etc.',
      '# - Volunteer Tags: Free text field for tags (e.g., "Parent Champion; Ward 3; Evening Batch")',
      '#   Can be used later for filtering/sorting. Multiple tags can be separated by semicolons or commas.',
      '# - Email: Optional, must be valid email format if provided',
      '# - Alternate Phone: Optional, must be 10 or 12 digits (after normalization)',
      '# - Notes: Optional, general notes about the volunteer',
      '# - Is Active: TRUE or FALSE (default: TRUE)',
      '#',
      '# Example rows:',
      '# Row 1: Basic volunteer with required fields',
      '# Row 2: Volunteer with Role and Volunteer Tags',
      '# Row 3: Update existing volunteer (by phone number)',
      '#'
    ];
    
    const headers = [
      'Name',
      'Role',
      'Phone',
      'Email',
      'Alternate Phone',
      'Volunteer Tags',
      'Notes',
      'Is Active'
    ];
    
    const exampleRow1 = [
      'Robert Johnson',
      'jef_coordinator',
      '9876543230',
      '',
      '',
      '',
      '',
      'TRUE'
    ];
    
    const exampleRow2 = [
      'Sarah Williams',
      'volunteer',
      '+91 98765 43231',
      'sarah.williams@example.com',
      '9876543232',
      'Parent Champion; Ward 3; Evening Batch',
      'Active parent volunteer',
      'TRUE'
    ];
    
    const exampleRow3 = [
      'Michael Brown',
      '',
      '9876543233',
      'michael.brown@example.com',
      '',
      'Ward 5',
      'JEF Coordinator for Ward 5',
      'TRUE'
    ];
    
    const csvContent = [
      ...instructions,
      headers.join(','),
      exampleRow1.join(','),
      exampleRow2.join(','),
      exampleRow3.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return blob;
  }

  // ==================== Classes API ====================

  /**
   * List classes with filters
   * @param {Object} params - { academicYear, schoolId, schoolCode, isActive }
   */
  async listClasses(params = {}) {
    const query = new URLSearchParams();
    if (params.academicYear) query.append('academic_year', params.academicYear);
    if (params.schoolId) query.append('school_id', params.schoolId);
    if (params.schoolCode) query.append('school_code', params.schoolCode);
    if (params.isActive !== undefined) query.append('is_active', params.isActive);
    // Pagination parameters
    const page = params.page || 1;
    const pageSize = params.pageSize || 20;
    query.append('page', page.toString());
    query.append('page_size', pageSize.toString());
    // Search parameter
    if (params.search) query.append('search', params.search);
    // Sort parameter
    if (params.sort) query.append('sort', params.sort);
    
    const url = `${API_BASE}/api/v1/read-along/classes/list?${query}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch classes' }));
      const errorMessage = error.error || error.details || 'Failed to fetch classes';
      
      // Provide more helpful error message if endpoint doesn't exist
      if (response.status === 400) {
        // 400 with "Invalid class ID" suggests route conflict: /classes/:id is matching /classes/list
        if (errorMessage.includes('Invalid class ID') || errorMessage.includes('class ID')) {
          throw new Error(`Backend route conflict: The endpoint /api/v1/read-along/classes/list is being matched by /classes/:id route. The backend needs to add a specific /classes/list route before the /classes/:id route, or use a different endpoint pattern.`);
        }
        throw new Error(`${errorMessage} (Endpoint may not be implemented: ${url})`);
      }
      if (response.status === 404) {
        throw new Error(`Endpoint not found: ${url}. The backend needs to implement GET /api/v1/read-along/classes/list`);
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }

  /**
   * Get class by ID
   * @param {number} id - Class ID
   */
  async getClass(id) {
    const response = await fetch(`${API_BASE}/api/v1/read-along/classes/by-id/${id}`);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch class' }));
      throw new Error(error.error || error.details || 'Failed to fetch class');
    }
    return response.json();
  }

  /**
   * Create class
   * @param {Object} classData - Class data { school_code, name, academic_year, is_active }
   */
  async createClass(classData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/classes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(classData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to create class' }));
      throw new Error(error.error || error.details || 'Failed to create class');
    }
    return response.json();
  }

  /**
   * Update class
   * @param {number} id - Class ID
   * @param {Object} classData - Class data
   */
  async updateClass(id, classData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/classes/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(classData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to update class' }));
      throw new Error(error.error || error.details || 'Failed to update class');
    }
    return response.json();
  }

  /**
   * Deactivate class (soft delete)
   * @param {number} id - Class ID
   */
  async deactivateClass(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    // Get current class data first to include required fields
    try {
      const classResponse = await this.getClass(id);
      const classItem = classResponse.class || classResponse;
      if (!classItem) {
        throw new Error("Class not found");
      }
      // Update with is_active = false, preserving all required fields
      return this.updateClass(id, {
        name: classItem.name,
        is_active: false
      });
    } catch (error) {
      // If we can't get the class, just try to update with is_active false
      // This should not happen in normal flow, but provides a fallback
      throw new Error("Failed to get class data for deactivation: " + error.message);
    }
  }

  /**
   * Permanently delete an inactive class
   * @param {number} id - Class ID
   */
  async deleteClass(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/classes/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `Failed to delete class: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Download classes template CSV
   */
  async downloadClassesTemplate() {
    const headers = [
      'School Code',
      'Class Name',
      'Academic Year',
      'Is Active'
    ];
    
    const exampleRow = [
      'S001',
      '3',
      '2024-25',
      'TRUE'
    ];
    
    const csvContent = [
      headers.join(','),
      exampleRow.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return blob;
  }

  // ==================== Divisions API ====================

  /**
   * List divisions with filters
   * @param {Object} params - { academicYear, schoolId, schoolCode, classId, className, isActive }
   */
  async listDivisions(params = {}) {
    const query = new URLSearchParams();
    if (params.academicYear) query.append('academic_year', params.academicYear);
    if (params.schoolId) query.append('school_id', params.schoolId);
    if (params.schoolCode) query.append('school_code', params.schoolCode);
    if (params.classId) query.append('class_id', params.classId);
    if (params.className) query.append('class_name', params.className);
    if (params.isActive !== undefined) query.append('is_active', params.isActive);
    if (params.medium) query.append('medium', params.medium);
    if (params.programType) query.append('program_type', params.programType);
    if (params.jefCoordinator) query.append('jef_coordinator', params.jefCoordinator);
    if (params.managementType) query.append('management_type', params.managementType);
    if (params.teacher) query.append('teacher', params.teacher);
    if (params.search) query.append('search', params.search);
    // Always pass page and pageSize to ensure backend uses pagination
    query.append('page', params.page || 1);
    query.append('page_size', params.pageSize || 20);
    if (params.sort) query.append('sort', params.sort);
    
    const url = `${API_BASE}/api/v1/read-along/divisions/list?${query}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch divisions' }));
      const errorMessage = error.error || error.details || 'Failed to fetch divisions';
      
      // Provide more helpful error message if endpoint doesn't exist
      if (response.status === 400) {
        // 400 with "Invalid division ID" suggests route conflict: /divisions/:id is matching /divisions/list
        if (errorMessage.includes('Invalid division ID') || errorMessage.includes('division ID')) {
          throw new Error(`Backend route conflict: The endpoint /api/v1/read-along/divisions/list is being matched by /divisions/:id route. The backend needs to add a specific /divisions/list route before the /divisions/:id route, or use a different endpoint pattern.`);
        }
        throw new Error(`${errorMessage} (Endpoint may not be implemented: ${url})`);
      }
      if (response.status === 404) {
        throw new Error(`Endpoint not found: ${url}. The backend needs to implement GET /api/v1/read-along/divisions/list`);
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }

  /**
   * Get division by ID
   * @param {number} id - Division ID
   */
  async getDivision(id) {
    // Backend implements GET /api/v1/read-along/divisions/:id (optionally with ?include_joins=true)
    // The previous /by-id/:id path does not exist and caused 404s during deactivate/delete flows.
    const response = await fetch(`${API_BASE}/api/v1/read-along/divisions/${id}?include_joins=true`);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch division' }));
      throw new Error(error.error || error.details || 'Failed to fetch division');
    }
    return response.json();
  }

  /**
   * Create division
   * @param {Object} divisionData - Division data { school_code, class_name, name, medium, academic_year, program_type, teacher_code, jef_coordinator_code, total_students, is_active }
   */
  async createDivision(divisionData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/divisions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(divisionData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to create division' }));
      throw new Error(error.error || error.details || 'Failed to create division');
    }
    return response.json();
  }

  /**
   * Update division
   * @param {number} id - Division ID
   * @param {Object} divisionData - Division data
   */
  async updateDivision(id, divisionData) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/divisions/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify(divisionData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please log out and log back in to refresh your API key.");
      }
      const error = await response.json().catch(() => ({ error: 'Failed to update division' }));
      throw new Error(error.error || error.details || 'Failed to update division');
    }
    return response.json();
  }

  /**
   * Deactivate division (soft delete)
   * @param {number} id - Division ID
   */
  async deactivateDivision(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    // Get current division data first to include required fields
    try {
      const division = await this.getDivision(id);
      if (!division || !division.division) {
        throw new Error("Division not found");
      }
      const div = division.division;
      // Update with is_active = false, preserving all required fields
      return this.updateDivision(id, {
        name: div.name,
        medium: div.medium || "Marathi", // Default if missing
        program_type: div.program_type,
        teacher_code: div.teacher?.code,
        jef_coordinator_code: div.jef_coordinator?.code,
        total_students: div.total_students,
        is_active: false
      });
    } catch (error) {
      // If we can't get the division, try to update with minimal required fields
      // This is a fallback - ideally we should always have the division data
      return this.updateDivision(id, {
        name: "A", // Default name
        medium: "Marathi", // Default medium
        is_active: false
      });
    }
  }

  /**
   * Reactivate an inactive division
   * @param {number} id - Division ID
   * @param {Object} divisionData - Optional division data from list (to avoid extra API call)
   */
  async reactivateDivision(id, divisionData = null) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }

    let div = divisionData;

    if (!div) {
      try {
        const divisionResponse = await this.getDivision(id);
        div = divisionResponse.division;
        if (!div) {
          throw new Error("Division not found");
        }
      } catch (error) {
        throw new Error("Failed to get division data for reactivation: " + error.message);
      }
    }

    return this.updateDivision(id, {
      name: div.name,
      medium: div.medium || "Marathi",
      program_type: div.program_type,
      teacher_code: div.teacher?.code,
      jef_coordinator_code: div.jef_coordinator?.code,
      total_students: div.total_students,
      is_active: true
    });
  }

  /**
   * Permanently delete an inactive division
   * @param {number} id - Division ID
   */
  async deleteDivision(id) {
    const apiKey = this.getAuthToken();
    if (!apiKey || apiKey === "valid-session") {
      throw new Error("Authentication required. Please log out and log back in to refresh your session.");
    }
    
    const response = await fetch(`${API_BASE}/api/v1/read-along/divisions/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || `Failed to delete division: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Download divisions template CSV
   */
  async downloadDivisionsTemplate() {
    // Instructions as CSV comments
    const instructions = [
      '# Divisions Bulk Upload Template',
      '# Instructions:',
      '# 1. Required fields: School Code, Class Name, Division Name, Academic Year',
      '# 2. Division Name must be 1 or 2 alphanumeric characters (e.g., A, B, A1, B2, 1A)',
      '# 3. Medium defaults to "Marathi" if not provided',
      '# 4. Program Type defaults to "school" if not provided',
      '# 5. For Teacher/JEF Coordinator: Provide Code of existing teacher/coordinator only',
      '# 6. Teacher and JEF Coordinator codes must exist in the system (cannot create new)',
      '# 7. Academic Year format: YYYY-YY (e.g., 2025-26)',
      '# 8. Is Active: TRUE or FALSE (defaults to TRUE)',
      '#',
      '# Example rows below:',
      '#'
    ];
    
    const headers = [
      'School Code',
      'Class Name',
      'Division Name',
      'Academic Year',
      'Medium',
      'Program Type',
      'Teacher Code',
      'JEF Coordinator Code',
      'Number of Students',
      'Is Active'
    ];
    
    // Example 1: Basic division (no teacher or coordinator)
    const exampleRow1 = [
      'S001',
      '3',
      'A',
      '2025-26',
      'Marathi',
      'school',
      '',
      '',
      '30',
      'TRUE'
    ];
    
    // Example 2: Division with existing teacher and coordinator
    const exampleRow2 = [
      'S001',
      '3',
      'B',
      '2025-26',
      'Marathi',
      'school',
      'T001',
      'JEF001',
      '25',
      'TRUE'
    ];
    
    // Example 3: Division with only teacher
    const exampleRow3 = [
      'S002',
      '4',
      'A',
      '2025-26',
      'English',
      'center',
      'T002',
      '',
      '35',
      'TRUE'
    ];
    
    const csvContent = [
      ...instructions,
      headers.join(','),
      exampleRow1.join(','),
      exampleRow2.join(','),
      exampleRow3.join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return blob;
  }

  // ==================== User Role Management API ====================

  /**
   * List all available roles
   * @param {boolean} activeOnly - Only return active roles
   */
  async listAllRoles(activeOnly = false) {
    const query = activeOnly ? '?active_only=true' : '';
    const response = await fetch(`${API_BASE}/api/v1/admin/roles${query}`, {
      headers: {
        'X-API-Key': this.getAuthToken()
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch roles' }));
      throw new Error(error.error || error.details || 'Failed to fetch roles');
    }
    return response.json();
  }

  /**
   * Get roles for a user
   * @param {string} userType - 'teacher', 'officer', 'principal'
   * @param {number} userID - User ID
   */
  async getUserRoles(userType, userID) {
    const query = new URLSearchParams({
      user_type: userType,
      user_id: userID.toString()
    });
    
    const response = await fetch(`${API_BASE}/api/v1/admin/user-roles?${query}`, {
      headers: {
        'X-API-Key': this.getAuthToken()
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch user roles' }));
      throw new Error(error.error || error.details || 'Failed to fetch user roles');
    }
    return response.json();
  }

  /**
   * Assign a role to a user
   * @param {string} userType - 'teacher', 'officer', 'principal'
   * @param {number} userID - User ID
   * @param {string} roleCode - Role code (e.g., 'teacher', 'principal', 'officer')
   */
  async assignRoleToUser(userType, userID, roleCode) {
    const response = await fetch(`${API_BASE}/api/v1/admin/user-roles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.getAuthToken()
      },
      body: JSON.stringify({
        user_type: userType,
        user_id: userID,
        role_code: roleCode
      })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to assign role' }));
      throw new Error(error.error || error.details || 'Failed to assign role');
    }
    return response.json();
  }

  /**
   * Remove a role from a user
   * @param {string} userType - 'teacher', 'officer', 'principal'
   * @param {number} userID - User ID
   * @param {string} roleCode - Role code to remove
   */
  async removeRoleFromUser(userType, userID, roleCode) {
    const query = new URLSearchParams({
      user_type: userType,
      user_id: userID.toString(),
      role_code: roleCode
    });
    
    const response = await fetch(`${API_BASE}/api/v1/admin/user-roles?${query}`, {
      method: 'DELETE',
      headers: {
        'X-API-Key': this.getAuthToken()
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to remove role' }));
      throw new Error(error.error || error.details || 'Failed to remove role');
    }
    return response.json();
  }

  /**
   * List all user assignments (admin only)
   * @param {object} filters - Optional filters { user_type, user_id, school_id, academic_year }
   */
  async listUserAssignments(filters = {}) {
    const query = new URLSearchParams();
    if (filters.user_type) query.append('user_type', filters.user_type);
    if (filters.user_id) query.append('user_id', filters.user_id.toString());
    if (filters.school_id) query.append('school_id', filters.school_id.toString());
    if (filters.academic_year) query.append('academic_year', filters.academic_year);
    
    const response = await fetch(`${API_BASE}/api/v1/admin/user-assignments?${query}`, {
      headers: {
        'X-API-Key': this.getAuthToken()
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch assignments' }));
      throw new Error(error.error || error.details || 'Failed to fetch assignments');
    }
    return response.json();
  }

  /**
   * Assign a user to a school/class/division
   * @param {object} assignment - { user_type, user_id, school_id, class_id?, division_id?, academic_year, assignment_type, notes? }
   */
  async assignUser(assignment) {
    const response = await fetch(`${API_BASE}/api/v1/admin/user-assignments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.getAuthToken()
      },
      body: JSON.stringify(assignment)
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to assign user' }));
      throw new Error(error.error || error.details || 'Failed to assign user');
    }
    return response.json();
  }

  /**
   * Remove a user assignment
   * @param {number} assignmentId - Assignment ID to remove
   */
  async removeUserAssignment(assignmentId) {
    const response = await fetch(`${API_BASE}/api/v1/admin/user-assignments/${assignmentId}`, {
      method: 'DELETE',
      headers: {
        'X-API-Key': this.getAuthToken()
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to remove assignment' }));
      throw new Error(error.error || error.details || 'Failed to remove assignment');
    }
    return response.json();
  }

  /**
   * Get all users (teachers, officers) for assignment management
   */
  async getAllUsersForAssignments() {
    try {
      const [teachers, officers] = await Promise.all([
        this.getTeachers(false).catch(() => ({ teachers: [] })),
        this.getOfficers(false).catch(() => ({ officers: [] }))
      ]);
      
      const users = [
        ...(teachers.teachers || []).map(t => ({ ...t, user_type: 'teacher' })),
        ...(officers.officers || []).map(o => ({ ...o, user_type: 'officer' }))
      ];
      
      return { users };
    } catch (error) {
      console.error('Failed to fetch users:', error);
      return { users: [] };
    }
  }

  // ============================================================================
  // User Authentication Methods (for Teachers, Officers, Principals)
  // ============================================================================
  // NOTE: User authentication has been migrated to AuthContext (System 1)
  // These methods are kept only for admin pages that need to check both admin and user tokens
  // For user authentication, use AuthContext via useAuth() hook

  /**
   * Get user authentication token
   * Checks both admin token (neev-auth-token) and regular user token from tokenManager
   * Used by admin pages that need to support both admin and user authentication
   */
  getUserAuthToken() {
    // Check for admin token first (used by admin/superadmin login)
    const adminToken = localStorage.getItem('neev-auth-token');
    if (adminToken) {
      return adminToken;
    }
    // Fall back to user token from tokenManager (System 1)
    return tokenManager.getAccessToken();
  }

  /**
   * Bulk upload schools from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "...", academicYear: "2025-26" }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadSchools(options = {}) {
    const token = this.getUserAuthToken();
    if (!token) {
      throw new Error('User not authenticated');
    }

    const body = {};
    if (options.googleSheetsUrl) {
      body.google_sheets_url = options.googleSheetsUrl;
    } else if (options.rows) {
      body.rows = options.rows;
    } else {
      throw new Error('Either rows or googleSheetsUrl must be provided');
    }

    if (options.academicYear) {
      body.academic_year = options.academicYear;
    }

    const response = await fetch(`${API_BASE}/api/v1/read-along/schools/bulk-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to upload schools' }));
      throw new Error(error.error || error.details || 'Failed to upload schools');
    }

    return response.json();
  }

  /**
   * Generic bulk upload method for any entity type
   * @param {string} entityType - Entity type (e.g., 'schools', 'teachers', 'divisions', 'officers')
   * @param {Object} options - { rows: [...], googleSheetsUrl: "...", academicYear?: "2025-26" }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUpload(entityType, options = {}) {
    // Bulk upload is an admin-only endpoint, so use getAuthToken() like other admin endpoints
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Admin authentication required. Please log in as admin.');
    }

    // Validate entity type
    const validEntityTypes = ['schools', 'teachers', 'divisions', 'officers', 'jef-volunteers', 'classes'];
    if (!validEntityTypes.includes(entityType)) {
      throw new Error(`Invalid entity type: ${entityType}. Valid types: ${validEntityTypes.join(', ')}`);
    }

    const body = {};
    if (options.googleSheetsUrl) {
      body.google_sheets_url = options.googleSheetsUrl;
    } else if (options.rows) {
      body.rows = options.rows;
    } else {
      throw new Error('Either rows or googleSheetsUrl must be provided');
    }

    // Add academic year if provided (mainly for schools)
    if (options.academicYear) {
      body.academic_year = options.academicYear;
    }

    // Add partial upload flag if provided
    if (options.partialUpload) {
      body.partial_upload = true;
    }

    // Construct endpoint URL based on entity type
    const endpoint = `/api/v1/read-along/${entityType}/bulk-upload`;

    // Admin bulk upload endpoints use X-API-Key header instead of Authorization Bearer
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Use X-API-Key for admin endpoints (bulk upload requires admin authentication)
    if (token) {
      headers['X-API-Key'] = token;
    }

    // Log request details for debugging
    console.log('Bulk upload request:', {
      endpoint: `${API_BASE}${endpoint}`,
      method: 'POST',
      headers: { ...headers, 'X-API-Key': token ? `${token.substring(0, 10)}...` : 'missing' },
      body: body,
      firstRow: options.rows && options.rows.length > 0 ? options.rows[0] : null
    });

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body)
    });

    // Parse response body first (backend may return data even with 400 status)
    let responseData;
    const contentType = response.headers.get('content-type');
    try {
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = { error: text || `HTTP ${response.status}: ${response.statusText}` };
      }
    } catch (parseError) {
      console.error('Failed to parse response:', parseError);
      responseData = { error: `HTTP ${response.status}: ${response.statusText}` };
    }
    
    // Log error response for debugging
    if (!response.ok) {
      console.error('Bulk upload error response:', {
        status: response.status,
        statusText: response.statusText,
        responseData: responseData
      });
    }

    // Backend returns 400 even for partial success (when result.Success is false but data exists)
    // Check if response contains valid bulk upload result data (not just an error with 'details')
    if (!response.ok) {
      // If response contains bulk upload result structure, treat it as success
      // Must have 'total' field (number) to distinguish from plain error responses
      // Plain errors have {error: "...", details: "..."} which should NOT match
      if (responseData && typeof responseData.total === 'number') {
        console.log('Backend returned result data with 400 status, treating as success');
        return responseData;
      }
      
      // Extract error message - handle various formats
      let errorMessage = `Failed to upload ${entityType} (${response.status})`;
      if (responseData) {
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else if (responseData.error) {
          errorMessage = typeof responseData.error === 'string' ? responseData.error : JSON.stringify(responseData.error);
        } else if (responseData.details) {
          errorMessage = typeof responseData.details === 'string' ? responseData.details : JSON.stringify(responseData.details);
        } else if (responseData.message) {
          errorMessage = typeof responseData.message === 'string' ? responseData.message : JSON.stringify(responseData.message);
        } else {
          errorMessage = JSON.stringify(responseData);
        }
      }
      
      console.error('Bulk upload error:', {
        status: response.status,
        statusText: response.statusText,
        errorData: responseData,
        errorMessage: errorMessage,
        url: `${API_BASE}${endpoint}`
      });
      throw new Error(errorMessage);
    }

    // Success response (200)
    return responseData;
  }

  /**
   * Bulk upload schools from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "...", academicYear: "2025-26" }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadSchools(options = {}) {
    return this.bulkUpload('schools', options);
  }

  /**
   * Bulk upload teachers from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "..." }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadTeachers(options = {}) {
    return this.bulkUpload('teachers', options);
  }

  /**
   * Bulk upload divisions from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "...", academicYear: "2025-26" }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadDivisions(options = {}) {
    return this.bulkUpload('divisions', options);
  }

  /**
   * Bulk upload officers from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "..." }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadOfficers(options = {}) {
    return this.bulkUpload('officers', options);
  }

  /**
   * Bulk upload JEF volunteers from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "..." }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadJEFVolunteers(options = {}) {
    return this.bulkUpload('jef-volunteers', options);
  }

  /**
   * Bulk upload lesson content from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "..." }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadLessonContent(options = {}) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Admin authentication required. Please log in as admin.');
    }

    const response = await fetch(`${API_BASE}/api/v1/teacher-activity/lesson-content/bulk-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token
      },
      body: JSON.stringify(options)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      console.error('Bulk upload lesson content error response:', errorData);
      
      // Extract error message from various possible formats
      let errorMessage = 'Failed to bulk upload lesson content';
      
      // Check if there are specific errors in the response
      if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        const firstErrors = errorData.errors.slice(0, 5); // Show first 5 errors
        const errorMessages = firstErrors.map(e => `Row ${e.row}: ${e.message || e.field || JSON.stringify(e)}`).join('\n');
        errorMessage = `Bulk upload failed with ${errorData.errors.length} error(s):\n${errorMessages}`;
        if (errorData.errors.length > 5) {
          errorMessage += `\n... and ${errorData.errors.length - 5} more errors. Check console for details.`;
        }
      } else if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
      } else if (errorData.details) {
        errorMessage = typeof errorData.details === 'string' ? errorData.details : JSON.stringify(errorData.details);
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else {
        errorMessage = JSON.stringify(errorData);
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  /**
   * Bulk upload exam results from CSV rows
   * Uses exam-specific endpoint (not generic bulk-upload)
   * @param {Object} options - { rows, partialUpload, academic_year }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadExamResults(options = {}) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Admin authentication required. Please log in as admin.');
    }

    const response = await fetch(`${API_BASE}/api/admin/exams/results/bulk-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token,
      },
      body: JSON.stringify({
        rows: options.rows,
        partial_upload: options.partialUpload || false,
        academic_year: options.academic_year,
        assessment_category: options.assessment_category || 'written',
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      // If response contains bulk upload result structure, treat as success
      if (data && typeof data.total === 'number') {
        return data;
      }
      throw new Error(data.error || `Request failed (${response.status})`);
    }

    return response.json();
  }

  /**
   * Merge duplicate JEF coordinators
   * @param {boolean} dryRun - If true, only preview without making changes
   * @returns {Promise<Object>} MergeResult
   */
  async mergeDuplicateCoordinators(dryRun = false) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Admin authentication required. Please log in as admin.');
    }

    const endpoint = `/api/v1/read-along/admin/merge-duplicate-coordinators${dryRun ? '?dry_run=true' : ''}`;
    
    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key': token
    };

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.details || `Failed to merge duplicate coordinators (${response.status})`);
    }

    return await response.json();
  }

  /**
   * Bulk upload classes from CSV rows or Google Sheets URL
   * @param {Object} options - { rows: [...], googleSheetsUrl: "...", academicYear: "2025-26" }
   * @returns {Promise<Object>} BulkUploadResult
   */
  async bulkUploadClasses(options = {}) {
    return this.bulkUpload('classes', options);
  }

  // ============================================================================
  // Lesson Content API Methods
  // ============================================================================

  /**
   * List lesson content with filters
   * @param {Object} params - { class_level, subject_id, subtopic_id, content_type, active_only, reportable_only, page, pageSize, sort }
   * @returns {Promise<Object>} { lesson_content: [...], total, page, total_pages }
   */
  async listLessonContent(params = {}) {
    const query = new URLSearchParams();
    if (params.class_level) query.append('class_level', params.class_level);
    if (params.subject_id) query.append('subject_id', params.subject_id);
    if (params.subtopic_id) query.append('subtopic_id', params.subtopic_id);
    if (params.content_type) query.append('content_type', params.content_type);
    if (params.active_only) query.append('active_only', 'true');
    if (params.reportable_only) query.append('reportable_only', 'true');
    if (params.page) query.append('page', params.page);
    if (params.pageSize) query.append('page_size', params.pageSize);
    if (params.sort) query.append('sort', params.sort);

    const token = this.getAuthToken();
    if (!token || token === "valid-session") {
      throw new Error("Authentication required. Please log in as admin.");
    }

    const response = await fetch(`${API_BASE}/api/v1/teacher-activity/lesson-content?${query}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.details || `Failed to list lesson content (${response.status})`);
    }

    return await response.json();
  }

  /**
   * Get lesson content by ID
   * @param {number} id - Lesson content ID
   * @param {boolean} includeJoins - Include subject and subtopic data
   * @returns {Promise<Object>} Lesson content object
   */
  async getLessonContent(id, includeJoins = false) {
    const query = new URLSearchParams();
    if (includeJoins) query.append('include_joins', 'true');

    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Admin authentication required. Please log in as admin.');
    }

    const response = await fetch(`${API_BASE}/api/v1/teacher-activity/lesson-content/${id}?${query}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.details || `Failed to get lesson content (${response.status})`);
    }

    const data = await response.json();
    return data.lesson_content;
  }

  /**
   * Create new lesson content
   * @param {Object} lessonContentData - Lesson content data
   * @returns {Promise<Object>} Created lesson content
   */
  async createLessonContent(lessonContentData) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Admin authentication required. Please log in as admin.');
    }

    const response = await fetch(`${API_BASE}/api/v1/teacher-activity/lesson-content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token
      },
      body: JSON.stringify(lessonContentData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.details || `Failed to create lesson content (${response.status})`);
    }

    const data = await response.json();
    return data.lesson_content;
  }

  /**
   * Update lesson content
   * @param {number} id - Lesson content ID
   * @param {Object} lessonContentData - Updated lesson content data
   * @returns {Promise<Object>} Updated lesson content
   */
  async updateLessonContent(id, lessonContentData) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Admin authentication required. Please log in as admin.');
    }

    const response = await fetch(`${API_BASE}/api/v1/teacher-activity/lesson-content/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token
      },
      body: JSON.stringify(lessonContentData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.details || `Failed to update lesson content (${response.status})`);
    }

    const data = await response.json();
    return data.lesson_content;
  }

  /**
   * List subjects (for dropdown)
   * @param {boolean} activeOnly - Only return active subjects
   * @returns {Promise<Array>} Array of subjects
   */
  async listSubjects(activeOnly = true) {
    const query = new URLSearchParams();
    if (activeOnly) query.append('active_only', 'true');

    const token = this.getAuthToken();
    if (!token || token === "valid-session") {
      throw new Error("Authentication required. Please log in as admin.");
    }

    const response = await fetch(`${API_BASE}/api/v1/teacher-activity/subjects?${query}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.details || `Failed to list subjects (${response.status})`);
    }

    const data = await response.json();
    return data.subjects || [];
  }

  /**
   * List subtopics for a subject
   * @param {number} subjectId - Subject ID
   * @param {boolean} activeOnly - Only return active subtopics
   * @returns {Promise<Array>} Array of subtopics
   */
  async listSubtopics(subjectId, activeOnly = true) {
    const query = new URLSearchParams();
    query.append('subject_id', subjectId);
    if (activeOnly) query.append('active_only', 'true');

    const token = this.getAuthToken();
    if (!token || token === "valid-session") {
      throw new Error("Authentication required. Please log in as admin.");
    }

    const response = await fetch(`${API_BASE}/api/v1/teacher-activity/subtopics?${query}`, {
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': token
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}: ${response.statusText}` }));
      throw new Error(errorData.error || errorData.details || `Failed to list subtopics (${response.status})`);
    }

    const data = await response.json();
    return data.subtopics || [];
  }
  // ============================================
  // GATHAS (Mindful Content)
  // ============================================

  async listActiveGathas(type = '') {
    const query = type ? `?type=${encodeURIComponent(type)}` : '';
    const response = await fetch(`${API_BASE}/api/v1/gathas${query}`);
    if (!response.ok) {
      throw new Error('Failed to fetch gathas');
    }
    const data = await response.json();
    return data.data || [];
  }

  async listAllGathas() {
    const apiKey = this.getAuthToken();
    const response = await fetch(`${API_BASE}/api/admin/gathas`, {
      headers: { 'X-API-Key': apiKey }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch gathas');
    }
    const data = await response.json();
    return data.data || [];
  }

  async createGatha(gathaData) {
    const apiKey = this.getAuthToken();
    const response = await fetch(`${API_BASE}/api/admin/gathas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
      body: JSON.stringify(gathaData)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create gatha' }));
      throw new Error(error.error || error.details || 'Failed to create gatha');
    }
    return response.json();
  }

  async updateGatha(id, gathaData) {
    const apiKey = this.getAuthToken();
    const response = await fetch(`${API_BASE}/api/admin/gathas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
      body: JSON.stringify(gathaData)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update gatha' }));
      throw new Error(error.error || error.details || 'Failed to update gatha');
    }
    return response.json();
  }

  async deleteGatha(id) {
    const apiKey = this.getAuthToken();
    const response = await fetch(`${API_BASE}/api/admin/gathas/${id}`, {
      method: 'DELETE',
      headers: { 'X-API-Key': apiKey }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete gatha' }));
      throw new Error(error.error || error.details || 'Failed to delete gatha');
    }
  }

  // ── Year Rollover ─────────────────────────────────────────────────────────

  async _rolloverFetch(path, options = {}) {
    const token = this.getAuthToken();
    const username = localStorage.getItem('neev-auth-username') || '';
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: { 'X-API-Key': token, 'X-User': username, ...(options.headers || {}) },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || res.statusText);
    }
    return res.json();
  }

  async getRolloverStatus()     { return this._rolloverFetch('/api/admin/year-rollover/status'); }
  async runRolloverPreflight()  { return this._rolloverFetch('/api/admin/year-rollover/preflight'); }
  async copyRolloverStructure() { return this._rolloverFetch('/api/admin/year-rollover/copy-structure',         { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }
  async revokeRolloverAccess()  { return this._rolloverFetch('/api/admin/year-rollover/revoke-teacher-access',  { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }
  async activateRolloverYear()  { return this._rolloverFetch('/api/admin/year-rollover/activate',               { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }
  async undoRolloverActivation(){ return this._rolloverFetch('/api/admin/year-rollover/undo-activation',        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }
  async skipRolloverTeacherImport()  { return this._rolloverFetch('/api/admin/year-rollover/skip-teacher-import', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }
  async skipRolloverRevokeAccess()   { return this._rolloverFetch('/api/admin/year-rollover/skip-revoke-access',   { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }
  async advanceRolloverSchools()     { return this._rolloverFetch('/api/admin/year-rollover/advance-schools',       { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }
  async getWorkflowStatusBreakdown() { return this._rolloverFetch('/api/admin/year-rollover/workflow-status-breakdown'); }
  async cancelStaleWorkflows()       { return this._rolloverFetch('/api/admin/year-rollover/cancel-stale-workflows', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); }

  async downloadRolloverTemplate() {
    const token = this.getAuthToken();
    const res = await fetch(`${API_BASE}/api/admin/year-rollover/teacher-import-template`, {
      headers: { 'X-API-Key': token },
    });
    if (!res.ok) throw new Error('Template download failed');
    return res.blob();
  }

  async bulkCreateRolloverTeachers(file, dryRun = false) {
    const token = this.getAuthToken();
    const form = new FormData();
    form.append('file', file);
    const url = `${API_BASE}/api/admin/year-rollover/bulk-create-teachers${dryRun ? '?dry_run=true' : ''}`;
    const res = await fetch(url, { method: 'POST', headers: { 'X-API-Key': token }, body: form });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || res.statusText);
    }
    return res.json();
  }
}

export default new ReadAlongAPI();

